import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Operator } from '@/lib/api';
import { cn } from '@/lib/utils';
import { LayoutDashboard, Server, Users, UserX, Activity, KeyRound, LogOut, Settings, Sparkles, UserCog, UserCircle, Layers, Wrench, Menu, X } from 'lucide-react';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/servers', label: 'Servers', icon: Server },
  { to: '/tenants', label: 'Tenants', icon: Users },
  { to: '/cleanup', label: 'Cleanup', icon: UserX },
  { to: '/tiers', label: 'Tiers', icon: Layers },
  { to: '/tools', label: 'Tools', icon: Wrench },
  { to: '/settings', label: 'Settings', icon: Settings },
  { to: '/skills', label: 'Skills', icon: Sparkles },
  { to: '/operators', label: 'Operators', icon: UserCog },
  { to: '/ssh-keys', label: 'SSH Keys', icon: KeyRound },
  { to: '/activity', label: 'Activity', icon: Activity },
];

const SECONDARY_NAV = [
  { to: '/account', label: 'Account', icon: UserCircle },
];

export function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [navOpen, setNavOpen] = useState(false);

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<{ operator: Operator }>('/api/auth/me'),
    retry: false,
  });

  const logout = useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSuccess: () => {
      qc.clear();
      navigate('/login');
    },
  });

  useEffect(() => {
    if (meQuery.isError) {
      navigate('/login', { replace: true });
    }
  }, [meQuery.isError, navigate]);

  // Close the mobile drawer whenever the route changes (e.g. tapping a nav link
  // or an in-page Link). Keyed on the full location so back/forward closes it too.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Escape closes the drawer; lock body scroll while it's open so the page
  // behind doesn't move under the overlay.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [navOpen]);

  if (meQuery.isLoading || meQuery.isError) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  }

  const operator = meQuery.data!.operator;

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
      isActive ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
    );

  return (
    <div className="min-h-screen lg:flex">
      {/* Drawer backdrop — mobile only, behind the sidebar, above page content. */}
      {navOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar: off-canvas drawer on mobile, static column on desktop. */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-slate-200 flex flex-col transition-transform duration-200 ease-out',
          'lg:static lg:z-auto lg:w-56 lg:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="px-5 py-5 border-b border-slate-800 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white">Control Plane</div>
            <div className="text-xs text-slate-400 mt-0.5 truncate" title={operator.email}>
              {operator.email}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            className="lg:hidden text-slate-400 hover:text-white transition-colors -mr-1"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={navLinkClass}>
              <Icon size={16} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-2 py-3 border-t border-slate-800 space-y-1">
          {SECONDARY_NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={navLinkClass}>
              <Icon size={16} />
              <span>{label}</span>
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => logout.mutate()}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut size={16} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Right column: mobile top bar + scrollable content. min-w-0 lets wide
          tables scroll inside their own container instead of stretching the page. */}
      <div className="flex flex-col flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 h-14 px-4 border-b border-slate-200 bg-white/90 backdrop-blur-sm">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            className="text-slate-600 hover:text-slate-900 transition-colors -ml-1"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
          <span className="text-sm font-semibold text-slate-900">Control Plane</span>
        </header>
        <main className="flex-1 min-w-0 overflow-auto">
          <div className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
